import administrativeBoundariesApi from '@/api/administrative-boundaries/administrativeBoundaries'
import {
  getPersistentMapCache,
  setPersistentMapCache,
  removePersistentMapCache,
  MAP_DATA_PERSISTENT_TTL_MS,
} from '@/utils/mapDataPersistentCache'

export const DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR = 2026

export const ADMINISTRATIVE_DETERMINE_NOT_FOUND = 'لم يتم الاستدلال'

export const ADMINISTRATIVE_DETERMINE_LAYERS = [
  { type: 'governorate', label: 'محافظة' },
  { type: 'center', label: 'مركز' },
  { type: 'city', label: 'مدينة' },
  { type: 'district', label: 'قرية' },
  { type: 'urban_zone', label: 'الحيز العمراني' },
  { type: 'reference_squares_new', label: 'مربع ارجاع (خرائط جديدة)' },
  { type: 'reference_squares_old', label: 'مربع ارجاع (خرائط قديمة)' },
]

export const ADMINISTRATIVE_DETERMINE_TYPES = ADMINISTRATIVE_DETERMINE_LAYERS.map(item => item.type)

export const ADMINISTRATIVE_BOUNDARY_TYPES = [
  {
    type: 'governorate',
    title: 'محافظات',
    label: 'Governorates',
    category: 'administrative',
    model_type: 'governorate',
    color: [0, 120, 200, 0.08],
    outline: [0, 90, 160, 0.9],
    googleFill: '#0078C8',
    googleStroke: '#005AA0',
    googleFillOpacity: 0.08,
  },
  {
    type: 'center',
    title: 'مراكز',
    label: 'Centers',
    category: 'administrative',
    model_type: 'center',
    color: [0, 160, 80, 0.08],
    outline: [0, 120, 60, 0.9],
    googleFill: '#00A050',
    googleStroke: '#00783C',
    googleFillOpacity: 0.08,
  },
  {
    type: 'city',
    title: 'مدن',
    label: 'Cities',
    category: 'administrative',
    model_type: 'city',
    color: [180, 80, 0, 0.08],
    outline: [140, 60, 0, 0.9],
    googleFill: '#B45000',
    googleStroke: '#8C3C00',
    googleFillOpacity: 0.08,
  },
  {
    type: 'district',
    title: 'قري',
    label: 'Districts',
    category: 'administrative',
    model_type: 'district',
    color: [0, 140, 150, 0.08],
    outline: [0, 100, 110, 0.9],
    googleFill: '#008C96',
    googleStroke: '#00646E',
    googleFillOpacity: 0.08,
  },
  {
    type: 'reference_squares_new',
    title: 'مربعات الارجاع — خرائط جديدة',
    label: 'Reference squares (new maps)',
    category: 'grid',
    model_type: null,
    color: [120, 0, 160, 0.06],
    outline: [90, 0, 120, 0.9],
    googleFill: '#7800A0',
    googleStroke: '#5A0078',
    googleFillOpacity: 0.06,
  },
  {
    type: 'reference_squares_old',
    title: 'مربعات الارجاع — خرائط قديمة',
    label: 'Reference squares (old maps)',
    category: 'grid',
    model_type: null,
    color: [100, 100, 100, 0.06],
    outline: [70, 70, 70, 0.9],
    googleFill: '#646464',
    googleStroke: '#464646',
    googleFillOpacity: 0.06,
  },
  {
    type: 'urban_zone',
    title: 'الأحوزة العمرانية',
    label: 'Urban zones',
    category: 'urban',
    model_type: null,
    color: [220, 120, 0, 0.10],
    outline: [180, 70, 0, 0.9],
    googleFill: '#DC7800',
    googleStroke: '#B44600',
    googleFillOpacity: 0.10,
  },
]

export const ADMINISTRATIVE_BOUNDARY_LAYER_KEY_PREFIX = 'administrativeBoundaries_'

const ADMINISTRATIVE_TYPE_SPECIFICITY = {
  district: 4,
  city: 3,
  center: 2,
  governorate: 1,
}

const cacheByKey = new Map()
let typesCache = null
let typesInFlight = null

export function administrativeBoundaryLayerKey(type) {
  return `${ADMINISTRATIVE_BOUNDARY_LAYER_KEY_PREFIX}${type}`
}

export function administrativeBoundaryLayerId(type) {
  return `administrative-boundaries-${type}`
}

export function isAdministrativeBoundaryLayerKey(key) {
  return typeof key === 'string' && key.startsWith(ADMINISTRATIVE_BOUNDARY_LAYER_KEY_PREFIX)
}

export function typeFromAdministrativeBoundaryLayerKey(key) {
  if (!isAdministrativeBoundaryLayerKey(key)) return null
  return key.slice(ADMINISTRATIVE_BOUNDARY_LAYER_KEY_PREFIX.length) || null
}

export function getBoundaryTypeMeta(type) {
  return ADMINISTRATIVE_BOUNDARY_TYPES.find(item => item.type === type) || null
}

export function getAdministrativeBoundaryLayerLoadItems() {
  return ADMINISTRATIVE_BOUNDARY_TYPES.map(item => ({
    key: administrativeBoundaryLayerKey(item.type),
    title: item.title,
    administrativeBoundaryType: item.type,
  }))
}

export function createAdministrativeBoundaryVisibility(defaultVisible = false) {
  return ADMINISTRATIVE_BOUNDARY_TYPES.reduce((acc, item) => {
    acc[administrativeBoundaryLayerKey(item.type)] = defaultVisible
    return acc
  }, {})
}

export function getAdministrativeBoundaryFillSymbol(type) {
  const meta = getBoundaryTypeMeta(type)
  return {
    type: 'simple-fill',
    color: meta?.color ?? [0, 120, 200, 0.08],
    outline: {
      type: 'simple-line',
      color: meta?.outline ?? [0, 90, 160, 0.9],
      width: 1.5,
    },
  }
}

export function getAdministrativeBoundaryGoogleStyle(type) {
  const meta = getBoundaryTypeMeta(type)
  return {
    strokeColor: meta?.googleStroke ?? '#005AA0',
    strokeOpacity: 0.9,
    strokeWeight: 1.5,
    fillColor: meta?.googleFill ?? '#0078C8',
    fillOpacity: meta?.googleFillOpacity ?? 0.08,
  }
}

function parseHexColor(value) {
  const match = /^#?([0-9a-f]{6})$/i.exec(String(value ?? '').trim())
  if (!match) return null
  const hex = match[1]
  return {
    hex: `#${hex.toUpperCase()}`,
    rgb: [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ],
  }
}

function boundedNumber(value, fallback, min, max) {
  const num = Number(value)
  if (!Number.isFinite(num)) return fallback
  return Math.min(max, Math.max(min, num))
}

/**
 * Styling captured from the uploaded KML/KMZ, if the importer found a <Style> for the feature.
 * Returns null when the feature carries none, so callers keep the layer-level palette.
 */
export function featureStyleFromAttributes(attrs) {
  const fill = parseHexColor(attrs?.style_fill_color)
  const stroke = parseHexColor(attrs?.style_stroke_color)
  if (!fill && !stroke) return null

  return {
    key: attrs.style_id
      || `${fill?.hex || ''}|${stroke?.hex || ''}|${attrs.style_fill_opacity || ''}|${attrs.style_stroke_width || ''}`,
    fill,
    stroke,
    fillOpacity: boundedNumber(attrs.style_fill_opacity, 0.1, 0, 1),
    strokeOpacity: boundedNumber(attrs.style_stroke_opacity, 0.9, 0, 1),
    strokeWidth: boundedNumber(attrs.style_stroke_width, 1.5, 0, 10),
  }
}

/**
 * Per-feature symbol resolver, memoized by style so a layer reuses one symbol object per distinct style.
 */
export function createAdministrativeBoundaryFillSymbolResolver(type) {
  const fallback = getAdministrativeBoundaryFillSymbol(type)
  const cache = new Map()

  return attrs => {
    const style = featureStyleFromAttributes(attrs)
    if (!style) return fallback

    if (!cache.has(style.key)) {
      cache.set(style.key, {
        type: 'simple-fill',
        color: style.fill ? [...style.fill.rgb, style.fillOpacity] : fallback.color,
        outline: {
          type: 'simple-line',
          color: style.stroke ? [...style.stroke.rgb, style.strokeOpacity] : fallback.outline.color,
          width: style.strokeWidth,
        },
      })
    }

    return cache.get(style.key)
  }
}

/** Google Maps counterpart of createAdministrativeBoundaryFillSymbolResolver. */
export function createAdministrativeBoundaryGoogleStyleResolver(type) {
  const fallback = getAdministrativeBoundaryGoogleStyle(type)
  const cache = new Map()

  return attrs => {
    const style = featureStyleFromAttributes(attrs)
    if (!style) return fallback

    if (!cache.has(style.key)) {
      cache.set(style.key, {
        strokeColor: style.stroke?.hex || fallback.strokeColor,
        strokeOpacity: style.strokeOpacity,
        strokeWeight: style.strokeWidth,
        fillColor: style.fill?.hex || fallback.fillColor,
        fillOpacity: style.fillOpacity,
      })
    }

    return cache.get(style.key)
  }
}

/** @deprecated Use getAdministrativeBoundaryFillSymbol(type) */
export const ADMINISTRATIVE_BOUNDARIES_FILL_SYMBOL = getAdministrativeBoundaryFillSymbol('governorate')

/** @deprecated Use getAdministrativeBoundaryGoogleStyle(type) */
export const ADMINISTRATIVE_BOUNDARIES_GOOGLE_STYLE = getAdministrativeBoundaryGoogleStyle('governorate')

export function flattenAdministrativeBoundaryAttributes(attrs = {}, typeMeta = null) {
  const model = attrs.model && typeof attrs.model === 'object' ? attrs.model : null
  const type = attrs.boundary_type || attrs.type || typeMeta?.type || ''
  const resolvedMeta = typeMeta || getBoundaryTypeMeta(type)
  return {
    ...attrs,
    boundary_type: type,
    type_label_ar: attrs.type_label_ar || resolvedMeta?.title || type,
    type_label: attrs.type_label || resolvedMeta?.label || type,
    model_type: attrs.model_type ?? model?.model_type ?? null,
    model_id: attrs.model_id ?? model?.id ?? null,
    model_name_ar: model?.name_ar || '',
    model_name_en: model?.name_en || '',
  }
}

const POPUP_HIDDEN_KEYS = new Set([
  'id',
  'objectid',
  'geometry',
  'layer',
  'model',
  'model_type',
  'model_id',
  'model_name_ar',
  'model_name_en',
  // Keep KMZ attribute `type` (e.g. مدينة) visible; API layer uses boundary_type.
  'type_label',
  'type_label_ar',
  'boundary_type',
  'governorate_id',
  'center_id',
  'city_id',
  'district_id',
  // Denormalized copies — KMZ SHAPE_* / area_fadan stay visible.
  'shape_leng',
  'shape_area',
  'style_id',
  'style_fill_color',
  'style_fill_opacity',
  'style_stroke_color',
  'style_stroke_opacity',
  'style_stroke_width',
])

const KNOWN_BOUNDARY_TYPE_VALUES = new Set(
  ADMINISTRATIVE_BOUNDARY_TYPES.map(item => item.type),
)

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Build a popup / InfoWindow HTML that mirrors the KMZ description table:
 * title = feature name, then every imported attribute as key/value rows.
 */
export function buildAdministrativeBoundaryPopupHtml(attrs = {}, options = {}) {
  const title = attrs.name || options.fallbackTitle || 'حدود إدارية'
  const rows = []

  const metaRows = [
    ['النوع', attrs.type_label_ar || attrs.boundary_type],
    ['الكود', attrs.code],
    ['السنة', attrs.year],
    ['السجل المرتبط', attrs.model_name_ar],
  ]
  metaRows.forEach(([label, value]) => {
    if (value === null || value === undefined || value === '') return
    rows.push([label, value])
  })

  Object.keys(attrs).forEach(key => {
    if (POPUP_HIDDEN_KEYS.has(key) || key === 'name' || key === 'code' || key === 'year') return
    if (key.startsWith('style_')) return
    // Corrupt nested-table keys from older imports (e.g. "Name\\n\\nFID")
    if (/\n/.test(key) || key.length > 80) return
    const value = attrs[key]
    if (value === null || value === undefined || value === '') return
    if (typeof value === 'object') return
    // Skip API layer type values; keep KMZ `type` like "مدينة"
    if (key === 'type' && KNOWN_BOUNDARY_TYPE_VALUES.has(String(value))) return
    rows.push([key, value])
  })

  const body = rows.length
    ? `<table style="border-collapse:collapse;width:100%;font-size:12px;margin-top:6px">
        ${rows.map(([label, value]) => `
          <tr>
            <td style="padding:2px 8px 2px 0;white-space:nowrap;vertical-align:top;color:#555"><strong>${escapeHtml(label)}</strong></td>
            <td style="padding:2px 0;vertical-align:top">${escapeHtml(value)}</td>
          </tr>`).join('')}
      </table>`
    : ''

  return `<div dir="rtl" style="min-width:220px;max-width:360px;text-align:right">
    <div style="font-weight:700;margin-bottom:2px">${escapeHtml(title)}</div>
    ${body}
  </div>`
}

export function getAdministrativeBoundaryPopupTemplate() {
  return {
    title: '{name}',
    content({ graphic } = {}) {
      return buildAdministrativeBoundaryPopupHtml(graphic?.attributes || {})
    },
  }
}

/** @deprecated Use getAdministrativeBoundaryPopupTemplate() */
export const ADMINISTRATIVE_BOUNDARIES_POPUP_TEMPLATE = getAdministrativeBoundaryPopupTemplate()

function adminBoundariesPersistentKey(year, type) {
  const y = Number(year) || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR
  return type
    ? `admin-boundaries:${y}:${type}`
    : `admin-boundaries:${y}`
}

function cacheKey(year, type) {
  return `${Number(year) || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR}:${type || 'all'}`
}

export function clearAdministrativeBoundariesCache(year, type) {
  const y = year != null && year !== ''
    ? (Number(year) || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR)
    : null

  if (type) {
    const key = cacheKey(y || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR, type)
    cacheByKey.delete(key)
    removePersistentMapCache(adminBoundariesPersistentKey(y || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR, type))
      .catch(() => {})
    return
  }

  if (y != null) {
    ADMINISTRATIVE_BOUNDARY_TYPES.forEach(item => {
      cacheByKey.delete(cacheKey(y, item.type))
      removePersistentMapCache(adminBoundariesPersistentKey(y, item.type)).catch(() => {})
    })
    cacheByKey.delete(cacheKey(y, null))
    removePersistentMapCache(adminBoundariesPersistentKey(y)).catch(() => {})
    return
  }

  cacheByKey.clear()
  ADMINISTRATIVE_BOUNDARY_TYPES.forEach(item => {
    removePersistentMapCache(adminBoundariesPersistentKey(DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR, item.type))
      .catch(() => {})
  })
}

function unwrapFeatureCollection(payload) {
  if (!payload || typeof payload !== 'object') {
    return { type: 'FeatureCollection', features: [] }
  }
  if (payload.type === 'FeatureCollection' && Array.isArray(payload.features)) {
    return payload
  }
  if (Array.isArray(payload.features)) {
    return {
      type: 'FeatureCollection',
      total: payload.total,
      features: payload.features,
    }
  }
  // Axios interceptor already returns response.data; some callers pass { data: FeatureCollection }.
  if (payload.data && typeof payload.data === 'object') {
    return unwrapFeatureCollection(payload.data)
  }
  return { type: 'FeatureCollection', features: [] }
}

function featureCollectionFeatureCount(featureCollection) {
  if (!featureCollection || !Array.isArray(featureCollection.features)) return 0
  return featureCollection.features.length
}

function unwrapList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

function mergeTypesWithMeta(list) {
  if (!Array.isArray(list) || !list.length) {
    return ADMINISTRATIVE_BOUNDARY_TYPES.map(item => ({ ...item }))
  }

  const byValue = new Map(
    list
      .map(item => {
        const value = item?.value || item?.type
        return value ? [String(value), item] : null
      })
      .filter(Boolean),
  )

  return ADMINISTRATIVE_BOUNDARY_TYPES.map(meta => {
    const remote = byValue.get(meta.type)
    if (!remote) return { ...meta }
    return {
      ...meta,
      title: remote.label_ar || meta.title,
      label: remote.label || meta.label,
      category: remote.category || meta.category,
      model_type: remote.model_type === undefined ? meta.model_type : remote.model_type,
    }
  })
}

export async function fetchAdministrativeBoundaryTypes({ force = false } = {}) {
  if (!force && typesCache) return typesCache
  if (!force && typesInFlight) return typesInFlight

  const requestPromise = (async () => {
    try {
      const response = await administrativeBoundariesApi.getTypes()
      const payload = response?.data && typeof response.data === 'object' ? response.data : response
      typesCache = mergeTypesWithMeta(unwrapList(payload))
      return typesCache
    } catch (error) {
      try {
        const response = await administrativeBoundariesApi.getDataTypes()
        const payload = response?.data && typeof response.data === 'object' ? response.data : response
        typesCache = mergeTypesWithMeta(unwrapList(payload))
        return typesCache
      } catch (e) {
        typesCache = ADMINISTRATIVE_BOUNDARY_TYPES.map(item => ({ ...item }))
        return typesCache
      }
    }
  })().finally(() => {
    if (typesInFlight === requestPromise) {
      typesInFlight = null
    }
  })

  typesInFlight = requestPromise
  return requestPromise
}

/**
 * Fetch full-year administrative boundaries GeoJSON once per type and cache in memory.
 * Always pass `type`. Do not call on pan/zoom — only on first load or after upload (force).
 */
export async function fetchAdministrativeBoundariesGeoJson(
  year = DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
  { force = false, type } = {},
) {
  const y = Number(year) || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR
  if (!type) {
    return { type: 'FeatureCollection', features: [] }
  }

  const key = cacheKey(y, type)
  const persistKey = adminBoundariesPersistentKey(y, type)
  const cached = cacheByKey.get(key)

  // Empty caches are treated as a miss so a later import is not stuck behind a stale 0-feature entry.
  if (!force && featureCollectionFeatureCount(cached?.featureCollection) > 0) {
    return cached.featureCollection
  }

  if (!force && cached?.inFlight) {
    return cached.inFlight
  }

  if (!force) {
    const persistent = await getPersistentMapCache(persistKey, MAP_DATA_PERSISTENT_TTL_MS)
    if (featureCollectionFeatureCount(persistent?.data) > 0) {
      cacheByKey.set(key, {
        featureCollection: persistent.data,
        inFlight: null,
        fetchedAt: persistent.fetchedAt || Date.now(),
      })
      return persistent.data
    }
    if (persistent?.data) {
      removePersistentMapCache(persistKey).catch(() => {})
    }
  }

  const entry = { featureCollection: null, inFlight: null, fetchedAt: 0 }
  cacheByKey.set(key, entry)

  const requestPromise = administrativeBoundariesApi.getGeoJson({ year: y, type })
    .then(async response => {
      const data = response?.data && typeof response.data === 'object' ? response.data : response
      const featureCollection = unwrapFeatureCollection(data)
      entry.featureCollection = featureCollection
      entry.fetchedAt = Date.now()
      if (featureCollectionFeatureCount(featureCollection) > 0) {
        await setPersistentMapCache(persistKey, featureCollection)
      } else {
        removePersistentMapCache(persistKey).catch(() => {})
      }
      return featureCollection
    })
    .finally(() => {
      if (entry.inFlight === requestPromise) {
        entry.inFlight = null
      }
    })

  entry.inFlight = requestPromise
  return requestPromise
}

export async function fetchAllAdministrativeBoundaryTypes(
  year = DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
  { force = false } = {},
) {
  const results = await Promise.all(
    ADMINISTRATIVE_BOUNDARY_TYPES.map(async item => {
      try {
        const featureCollection = await fetchAdministrativeBoundariesGeoJson(year, {
          force,
          type: item.type,
        })
        return { type: item.type, featureCollection, error: null }
      } catch (error) {
        return { type: item.type, featureCollection: null, error }
      }
    }),
  )
  return results
}

export function ringsFromGeoJsonGeometry(geometry) {
  return geoJsonPolygonParts(geometry).flatMap(part => (Array.isArray(part) ? part : []))
}

/**
 * GeoJSON RFC 7946: exterior rings are counter-clockwise, holes clockwise.
 * ArcGIS polygons are the inverse: clockwise exterior, counter-clockwise holes.
 */
export function polygonRingsForArcGis(geometry) {
  const parts = geoJsonPolygonParts(geometry)
  const rings = []

  parts.forEach(part => {
    if (!Array.isArray(part) || !part.length) return

    part.forEach((rawRing, ringIndex) => {
      const ring = normalizeLonLatRing(rawRing)
      if (ring.length < 3) return
      rings.push(ensureRingWinding(ring, ringIndex === 0))
    })
  })

  return rings
}

function geoJsonPolygonParts(geometry) {
  if (!geometry) return []

  if (geometry.type === 'Polygon') {
    return Array.isArray(geometry.coordinates) ? [geometry.coordinates] : []
  }

  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates || []).filter(Array.isArray)
  }

  const coords = geometry.coordinates
  if (!Array.isArray(coords) || !coords.length) return []

  if (
    Array.isArray(coords[0])
    && Array.isArray(coords[0][0])
    && typeof coords[0][0][0] === 'number'
  ) {
    return [coords]
  }

  if (
    Array.isArray(coords[0])
    && Array.isArray(coords[0][0])
    && Array.isArray(coords[0][0][0])
    && typeof coords[0][0][0][0] === 'number'
  ) {
    return coords.filter(Array.isArray)
  }

  return []
}

function normalizeLonLatRing(ring) {
  if (!Array.isArray(ring)) return []

  return ring
    .map(point => {
      if (!Array.isArray(point) || point.length < 2) return null
      const lng = Number(point[0])
      const lat = Number(point[1])
      if (Number.isNaN(lng) || Number.isNaN(lat)) return null
      return [lng, lat]
    })
    .filter(Boolean)
}

function ringSignedArea(ring) {
  let area = 0
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    area += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1]
  }
  return area / 2
}

function ensureRingWinding(ring, clockwise) {
  const isClockwise = ringSignedArea(ring) < 0
  if (isClockwise === clockwise) return ring
  return [...ring].reverse()
}

export function mapAdministrativeBoundaryItemsToGraphics(items, type) {
  const resolveSymbol = createAdministrativeBoundaryFillSymbolResolver(type)
  const meta = getBoundaryTypeMeta(type)
  const popupTemplate = getAdministrativeBoundaryPopupTemplate()

  return (items || []).map(item => {
    const rings = polygonRingsForArcGis(item.geometry)
    if (!rings.length) return null
    return {
      geometry: {
        type: 'polygon',
        rings,
        spatialReference: { wkid: 4326 },
      },
      symbol: resolveSymbol(item.attributes),
      attributes: flattenAdministrativeBoundaryAttributes(item.attributes, meta),
      popupTemplate,
    }
  }).filter(Boolean)
}

/** Normalize FeatureCollection or /map/all-data `administrative_boundaries` array. */
export function normalizeAdministrativeBoundaryItems(source) {
  if (!source) return []

  if (source.type === 'FeatureCollection' && Array.isArray(source.features)) {
    return source.features.map(feature => ({
      geometry: feature.geometry,
      attributes: feature.properties || {},
    }))
  }

  if (Array.isArray(source)) {
    return source.map(item => ({
      geometry: item.geometry,
      attributes: item.attributes || item.properties || item,
    }))
  }

  return []
}

function resolveUploadArgs(typeOrOptions, maybeYear) {
  if (typeOrOptions && typeof typeOrOptions === 'object') {
    return {
      type: typeOrOptions.type,
      year: typeOrOptions.year ?? DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
    }
  }
  return {
    type: typeOrOptions,
    year: maybeYear ?? DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
  }
}

export async function uploadAdministrativeBoundaries(file, typeOrOptions, maybeYear) {
  const { type, year } = resolveUploadArgs(typeOrOptions, maybeYear)
  if (!type) {
    throw new Error('type is required')
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('type', String(type))
  if (year != null && year !== '') {
    formData.append('year', String(year))
  }
  const response = await administrativeBoundariesApi.upload(formData)
  const payload = response?.data && typeof response.data === 'object' ? response.data : response
  const inner = payload?.data && typeof payload.data === 'object' ? payload.data : payload
  if (!inner?.queued) {
    clearAdministrativeBoundariesCache(year, type)
  }
  return payload
}

/**
 * Determine every administrative / urban / grid layer for a project or explicit point.
 * Do not send type / types / limit — the backend always returns every layer.
 */
export async function determineAdministrativeBoundary(payload = {}) {
  const body = { ...payload }
  delete body.type
  delete body.types
  delete body.limit
  if (body.year == null || body.year === '') {
    body.year = DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR
  }
  const response = await administrativeBoundariesApi.determine(body)
  return response?.data && typeof response.data === 'object' ? response.data : response
}

export async function nearestAdministrativeBoundary(payload = {}) {
  const body = { ...payload }
  if (body.year == null || body.year === '') {
    body.year = DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR
  }
  const response = await administrativeBoundariesApi.nearest(body)
  return response?.data && typeof response.data === 'object' ? response.data : response
}

export async function determineAdministrativeLocation(payload = {}) {
  const result = await determineAdministrativeBoundary(payload)
  return extractDeterminedAdministrativeLocation(result)
}

export function getDetermineAdministrativeBoundaryErrorMessage(error) {
  const status = error?.response?.status
  const payload = error?.response?.data
  const code = payload?.error
    || payload?.code
    || payload?.errors?.code
    || payload?.data?.error
    || null
  const message = payload?.message || error?.message || null

  if (status === 404 || code === 'project_not_found') {
    return message || 'المشروع غير موجود'
  }

  if (status === 422) {
    if (code === 'project_coordinates_not_found') {
      return message || 'لا توجد إحداثيات صالحة للمشروع لتحديد الحدود الإدارية'
    }
    if (code === 'validation_failed') {
      return message || 'بيانات غير صالحة لتحديد الحدود الإدارية'
    }
    return message || 'تعذر تحديد الحدود الإدارية'
  }

  return message || 'فشل تحديد الحدود الإدارية'
}

function boundarySpecificity(boundary) {
  const type = boundary?.type || boundary?.boundary_type || boundary?.model_type || ''
  return ADMINISTRATIVE_TYPE_SPECIFICITY[type] || 0
}

function pickMatchedBoundary(boundaries) {
  if (!Array.isArray(boundaries) || !boundaries.length) return null
  return [...boundaries].sort((a, b) => boundarySpecificity(b) - boundarySpecificity(a))[0]
}

function unwrapDeterminePayload(result) {
  const data = result?.data && typeof result.data === 'object' ? result.data : result
  if (!data || typeof data !== 'object') return {}
  return data
}

function displayNameOrNotFound(value) {
  if (value == null) return ADMINISTRATIVE_DETERMINE_NOT_FOUND
  const name = String(value).trim()
  return name || ADMINISTRATIVE_DETERMINE_NOT_FOUND
}

function normalizeDetermineLayer(type, raw) {
  const layer = raw && typeof raw === 'object' ? raw : {}
  const found = layer.found === true
  const name = displayNameOrNotFound(layer.name)
  const message = displayNameOrNotFound(layer.message || name)
  const model = layer.model && typeof layer.model === 'object' ? layer.model : null
  const meta = getBoundaryTypeMeta(type)
  return {
    type,
    found,
    name,
    message,
    id: layer.id ?? null,
    code: layer.code ?? null,
    year: layer.year ?? null,
    model,
    modelType: layer.model_type || model?.model_type || null,
    modelId: layer.model_id ?? model?.id ?? null,
    modelName: model?.name_ar || model?.name_en || null,
    typeLabelAr: layer.type_label_ar || meta?.title || type,
    geometry: found ? (layer.geometry || null) : null,
    boundary: found ? layer : null,
  }
}

export function emptyAdministrativeDetermineRows() {
  return ADMINISTRATIVE_DETERMINE_LAYERS.map(item => ({
    type: item.type,
    label: item.label,
    found: false,
    name: ADMINISTRATIVE_DETERMINE_NOT_FOUND,
    message: ADMINISTRATIVE_DETERMINE_NOT_FOUND,
    modelId: null,
    modelType: null,
    geometry: null,
  }))
}

export function extractDeterminedAdministrativeLocation(result) {
  const data = unwrapDeterminePayload(result)
  const resultsSource = data.results && typeof data.results === 'object' ? data.results : {}
  const byType = {}
  const rows = ADMINISTRATIVE_DETERMINE_LAYERS.map(item => {
    const normalized = normalizeDetermineLayer(item.type, resultsSource[item.type])
    const row = {
      ...normalized,
      label: item.label,
    }
    byType[item.type] = row
    return row
  })

  const foundRows = rows.filter(row => row.found)
  const primary = pickMatchedBoundary(foundRows) || foundRows[0] || null

  return {
    ...data,
    results: byType,
    rows,
    byType,
    matches: foundRows,
    boundary: primary?.boundary || null,
    name: primary?.name || ADMINISTRATIVE_DETERMINE_NOT_FOUND,
    model: primary?.model || null,
    modelType: primary?.modelType || null,
    modelId: primary?.modelId ?? null,
    modelName: primary?.modelName || null,
    type: primary?.type || null,
    typeLabelAr: primary?.typeLabelAr || null,
    geometry: primary?.geometry || null,
  }
}

/** @deprecated Use extractDeterminedAdministrativeLocation — determine now returns every boundary type. */
export function extractMatchedAdministrativeBoundary(result) {
  const location = extractDeterminedAdministrativeLocation(result)
  return location.matches.length ? location : null
}

/** Stronger fill for a single matched boundary highlight on the map. */
export const MATCHED_ADMINISTRATIVE_BOUNDARY_FILL_SYMBOL = {
  type: 'simple-fill',
  color: [0, 140, 90, 0.18],
  outline: { type: 'simple-line', color: [0, 110, 70, 1], width: 2.5 },
}

export const MATCHED_ADMINISTRATIVE_BOUNDARY_GOOGLE_STYLE = {
  strokeColor: '#006E46',
  strokeOpacity: 1,
  strokeWeight: 2.5,
  fillColor: '#008C5A',
  fillOpacity: 0.18,
}
